import React, { useEffect, useState, useCallback } from 'react';
import { Users, Plus, Edit2, Trash2, AlertCircle, Filter, XCircle, Search } from 'lucide-react';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { useGroupStore } from '../../../core/services/GroupStore';
import { useDamStore } from '../../../core/services/DamStore';
import { IGroup, ICreateGroupRequest } from '../../../core/interfaces/IGroup';
import { Card, CardContent } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../shared/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';

const serviceContainer = ServiceContainer.getInstance();
const groupService = serviceContainer.getGroupService();
const damService = serviceContainer.getDamService();

export const GroupsPage: React.FC = () => {
  const { groups, isLoading: loading, error, setGroups, addGroup, updateGroup, removeGroup, setLoading, setError } = useGroupStore();
  const { dams, setDams } = useDamStore();

  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<IGroup | null>(null);
  const [selectedDamId, setSelectedDamId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<IGroup | null>(null);
  const [formData, setFormData] = useState<ICreateGroupRequest>({
    sigla: '',
    description: '',
    damId: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsData, damsData] = await Promise.all([
        groupService.getAllGroups(),
        damService.getAllDams(),
      ]);
      setGroups(groupsData);
      setDams(damsData);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [setGroups, setDams, setLoading, setError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (group: IGroup) => {
    setEditingGroup(group);
    setFormData({
      sigla: group.sigla,
      description: group.description || '',
      damId: group.damId,
    });
    setShowForm(true);
  };

  const handleDelete = (group: IGroup) => {
    if ((group.memberCount || 0) > 0) {
      setError('Não é possível excluir um grupo com contatos. Remova todos os contatos primeiro.');
      return;
    }
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      await groupService.deleteGroup(groupToDelete.id);
      removeGroup(groupToDelete.id);
      setShowDeleteModal(false);
      setGroupToDelete(null);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erro ao excluir grupo';
      setError(errorMessage);
      console.error(err);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setGroupToDelete(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.sigla.trim() || !formData.damId) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);
      if (editingGroup) {
        const updated = await groupService.updateGroup(editingGroup.id, {
          ...editingGroup,
          sigla: formData.sigla,
          description: formData.description,
          damId: formData.damId,
        });
        updateGroup(editingGroup.id, updated);
      } else {
        const created = await groupService.createGroup(formData);
        addGroup(created);
      }
      handleCloseForm();
      setError(null);
    } catch (err) {
      setError('Erro ao salvar grupo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingGroup(null);
    setFormData({ sigla: '', description: '', damId: '' });
  };

  const clearFilters = () => {
    setSelectedDamId('');
    setSearchTerm('');
  };

  const filteredGroups = groups.filter((group) => {
    const matchesDam = !selectedDamId || group.damId === selectedDamId;
    const matchesSearch = !searchTerm || 
      group.sigla.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.damName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDam && matchesSearch;
  });

  if (loading && !groups.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando grupos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-8 w-8 text-blue-600" />
            Gerenciamento de Grupos
          </h1>
          <p className="text-slate-600 mt-2">Organize contatos em grupos por barragem</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="h-11">
          <Plus className="h-5 w-5 mr-2" />
          Novo Grupo
        </Button>
      </div>

      {/* Alertas/Erros */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>✕</Button>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
          </div>
          {(selectedDamId || searchTerm) && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="searchTerm" className="text-sm font-medium text-slate-700">
              Pesquisar
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por sigla, descrição ou barragem..."
                className="h-11 pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="damFilter" className="text-sm font-medium text-slate-700">
              Filtrar por Barragem
            </Label>
            <div className="flex gap-2">
              <Select value={selectedDamId} onValueChange={setSelectedDamId}>
                <SelectTrigger id="damFilter" className="flex-1 h-11">
                  <SelectValue placeholder="Todas as barragens" />
                </SelectTrigger>
                <SelectContent>
                  {dams.map((dam) => (
                    <SelectItem key={dam.id} value={dam.id}>
                      {dam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedDamId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDamId('')}
                  className="px-3"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Tabela de Grupos */}
      <Card>
        <CardContent className="p-6">
          {filteredGroups.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {groups.length === 0 ? 'Nenhum grupo cadastrado' : 'Nenhum grupo encontrado'}
              </h3>
              <p className="text-slate-600 mb-4">
                {groups.length === 0 
                  ? 'Comece criando seu primeiro grupo de contatos'
                  : 'Tente ajustar os filtros de pesquisa'}
              </p>
              {groups.length === 0 && (
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeiro Grupo
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 max-h-[62vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Sigla</TableHead>
                    <TableHead className="font-semibold text-slate-700">Descrição</TableHead>
                    <TableHead className="font-semibold text-slate-700">Barragem</TableHead>
                    <TableHead className="font-semibold text-slate-700">Contatos</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.map((group) => (
                    <TableRow key={group.id} className="hover:bg-slate-50">
                      <TableCell className="font-semibold text-slate-900">{group.sigla}</TableCell>
                      <TableCell className="text-slate-600">{group.description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {group.damName}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-semibold">
                          {group.memberCount || 0} contato(s)
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(group)}
                            className="hover:bg-blue-50 hover:text-blue-600"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(group)}
                            disabled={(group.memberCount || 0) > 0}
                            className={`${
                              (group.memberCount || 0) > 0 
                                ? 'opacity-50 cursor-not-allowed' 
                                : 'hover:bg-red-50 hover:text-red-600'
                            }`}
                            title={(group.memberCount || 0) > 0 ? 'Não é possível excluir grupo com contatos' : 'Excluir grupo'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {editingGroup ? (
                <>
                  <Edit2 className="h-5 w-5 text-blue-600" />
                  Editar Grupo
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-blue-600" />
                  Novo Grupo
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="sigla" className="text-sm font-medium">
                Sigla *
              </Label>
              <Input
                id="sigla"
                type="text"
                value={formData.sigla}
                onChange={(e) => setFormData({ ...formData, sigla: e.target.value })}
                placeholder="Ex: GRP-001, Grupo A, etc."
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                Descrição
              </Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Adicione uma descrição para identificar melhor este grupo..."
                rows={3}
                className="w-full rounded-md border-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="damId" className="text-sm font-medium">
                Barragem *
              </Label>
              <Select
                value={formData.damId}
                onValueChange={(value) => setFormData({ ...formData, damId: value })}
                disabled={editingGroup !== null}
              >
                <SelectTrigger id="damId" className="h-11">
                  <SelectValue placeholder="Selecione a barragem do grupo" />
                </SelectTrigger>
                <SelectContent>
                  {dams.map((dam) => (
                    <SelectItem key={dam.id} value={dam.id}>
                      {dam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingGroup && (
                <p className="text-xs text-slate-500 mt-1">
                  💡 A barragem não pode ser alterada após a criação
                </p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="min-w-[100px]">
                {loading ? 'Salvando...' : editingGroup ? 'Atualizar' : 'Criar Grupo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Confirmar Exclusão</DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-slate-700">
              Tem certeza que deseja excluir o grupo{' '}
              <span className="font-semibold">{groupToDelete?.sigla}</span>?
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Esta ação não poderá ser desfeita.
            </p>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={loading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
